import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, type Pathway } from '@/api';

export function PathwaysList() {
  const [items, setItems] = useState<Pathway[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.pathways.list().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this pathway? Tracks must be moved or deleted first.')) return;
    try {
      await adminApi.pathways.delete(id);
      setItems((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const swap = direction === 'up' ? index - 1 : index + 1;
    if (swap < 0 || swap >= newItems.length) return;
    [newItems[index], newItems[swap]] = [newItems[swap]!, newItems[index]!];
    const ids = newItems.map((p) => p.id);
    try {
      await adminApi.pathways.reorder(ids);
      setItems(newItems);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reorder failed');
    }
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-slate-800">Pathways</h1>
        <Link
          to="/pathways/new"
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          Add pathway
        </Link>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Order</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase">Icon</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase w-24">Reorder</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {items.map((p, idx) => (
              <tr key={p.id}>
                <td className="px-4 py-3 text-slate-800 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-slate-600">{p.order}</td>
                <td className="px-4 py-3 text-slate-600">{p.icon ?? '-'}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleMove(idx, 'up')}
                      disabled={idx === 0}
                      className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50"
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMove(idx, 'down')}
                      disabled={idx === items.length - 1}
                      className="rounded border border-slate-300 px-2 py-0.5 text-xs disabled:opacity-40 hover:bg-slate-50"
                      title="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/pathways/${p.id}/edit`}
                    className="text-indigo-600 hover:underline text-sm font-medium mr-4"
                  >
                    Edit
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(p.id)}
                    className="text-red-600 hover:underline text-sm font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
